// Single owner of the page's iOS audio session category (`navigator.audioSession`,
// Safari 16.4+). Nothing else in the app may write `navigator.audioSession.type`.
//
// - Idle / prompt playback → 'playback': page audio ignores the ring/silent switch
//   and plays through the speaker, like a music or video app.
// - Mic capture → 'play-and-record': WebKit rejects capture (getUserMedia and
//   speech recognition) unless the type is 'auto' or 'play-and-record' at the
//   moment capture starts, so callers claim BEFORE starting capture, synchronously
//   inside the user's tap.
//
// Feature-detected: where the API is missing (desktop browsers, iOS < 16.4) every
// call is a no-op. Verified on iOS 27 with a device spike (issue #240).

import { diag } from './diag';

type AudioSessionType = 'auto' | 'playback' | 'play-and-record';

interface AudioSessionLike {
  type: string;
}

let captureClaims = 0;
let lastActivityAt: number | null = null;
// Bumped by resetAudioSessionForTests so releases from before a reset are ignored.
let generation = 0;

function now(): number {
  return typeof performance !== 'undefined'
    ? performance.now()
    : /* istanbul ignore next */ Date.now();
}

function getSession(): AudioSessionLike | null {
  /* istanbul ignore next -- SSR guard; navigator is always defined in browser */
  if (typeof navigator === 'undefined') return null;
  return (
    (navigator as Navigator & { audioSession?: AudioSessionLike })
      .audioSession ?? null
  );
}

function setType(type: AudioSessionType, label: string): void {
  lastActivityAt = now();
  const session = getSession();
  if (!session) {
    diag('audioSession:', label, '(no navigator.audioSession)');
    return;
  }
  try {
    session.type = type;
    diag('audioSession.type =', session.type, '←', label);
  } catch (err) {
    diag('audioSession.type set threw ←', label, err);
  }
}

/** True when the browser exposes `navigator.audioSession` (Safari 16.4+). */
export function hasAudioSessionApi(): boolean {
  return getSession() !== null;
}

/**
 * Declare that mic capture is about to start. Call synchronously BEFORE
 * `recognition.start()` / `getUserMedia()`. Returns an idempotent release
 * function; the session returns to 'playback' when the last claim is released.
 */
export function claimCapture(label: string): () => void {
  captureClaims += 1;
  setType('play-and-record', `claim: ${label}`);
  const claimGeneration = generation;
  let released = false;
  return (): void => {
    if (released || claimGeneration !== generation) return;
    released = true;
    captureClaims -= 1;
    if (captureClaims === 0) {
      setType('playback', `release: ${label}`);
    } else {
      lastActivityAt = now();
      diag(
        'audioSession: release',
        label,
        '— claims still held:',
        captureClaims
      );
    }
  };
}

/**
 * Call right before a prompt starts playing. Sets 'playback' unless mic capture
 * is claimed (switching away from 'play-and-record' would break the capture).
 */
export function preparePlayback(label: string): void {
  if (captureClaims > 0) {
    lastActivityAt = now();
    diag(
      'audioSession: playback while capture claimed, keeping play-and-record ←',
      label
    );
    return;
  }
  setType('playback', `playback: ${label}`);
}

/**
 * Like preparePlayback, but forces a real category change ('auto' then
 * 'playback'). After a screen lock iOS can deactivate page audio while the type
 * still reads 'playback'; re-writing the same value doesn't reactivate it
 * (issue #243). Call on the first playback after the page was hidden.
 */
export function reactivatePlayback(label: string): void {
  if (captureClaims > 0) {
    preparePlayback(label);
    return;
  }
  setType('auto', `reactivate: ${label}`);
  setType('playback', `reactivate: ${label}`);
}

/** Milliseconds since the last session change or playback, or null if none (diagnostics). */
export function msSinceAudioSessionActivity(): number | null {
  if (lastActivityAt === null) return null;
  return Math.round(now() - lastActivityAt);
}

/** Test-only: forget claims and activity between tests. */
export function resetAudioSessionForTests(): void {
  captureClaims = 0;
  lastActivityAt = null;
  generation += 1;
}
